/**
 * useAlertEngine — drives real-time alert detection inside the Operations Center.
 *
 * Responsibilities:
 * - Re-runs the alert engine whenever operational data changes OR every 15s
 * - Deduplicates against previously-seen alerts to avoid toast storms
 * - Fires `showActionToast` for new critical / SLA alerts
 * - Exposes computed + grouped alerts, dismiss, and resolve helpers
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  computeAlerts,
  computePredictiveAlerts,
  groupAlerts,
  logAlertEvent,
  type ComputedAlert,
  type AlertGroup,
} from '../services/alertEngine';
import type {
  OpsActiveWorkflow,
  OpsBottleneck,
  OpsCriticalProblem,
  OpsOverdueAction,
} from '../services/operationsCenterService';
import { useAppToast } from '../components/AppToast';

interface UseAlertEngineInput {
  workflows: OpsActiveWorkflow[];
  bottlenecks: OpsBottleneck[];
  problems: OpsCriticalProblem[];
  overdueActions: OpsOverdueAction[];
  avgStageMinutes?: number;
  /** Pass `true` to pause toast notifications (e.g., when user is not focused on OpsCenter) */
  silentMode?: boolean;
}

interface UseAlertEngineReturn {
  alerts: ComputedAlert[];
  groups: AlertGroup[];
  topCritical: ComputedAlert | null;
  dismissAlert: (id: string) => void;
  resolveAlert: (id: string) => void;
  /** Call this when user clicks an alert — logs the interaction */
  onAlertClick: (alert: ComputedAlert) => void;
}

const POLLING_MS = 15_000;

export function useAlertEngine({
  workflows,
  bottlenecks,
  problems,
  overdueActions,
  avgStageMinutes,
  silentMode = false,
}: UseAlertEngineInput): UseAlertEngineReturn {
  const navigate = useNavigate();
  const { showActionToast } = useAppToast();

  const [alerts, setAlerts] = useState<ComputedAlert[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [resolved, setResolved] = useState<Set<string>>(new Set());

  // Track which alert IDs have already triggered a toast
  const notifiedRef = useRef<Set<string>>(new Set());
  // Track generation timestamps for resolution-time calc
  const generatedAtRef = useRef<Map<string, number>>(new Map());
  // Previous bottleneck snapshot for predictive trending
  const prevBottlenecksRef = useRef<typeof bottlenecks>([]);

  const runEngine = useCallback(() => {
    const computed = computeAlerts({ workflows, bottlenecks, problems, overdueActions, avgStageMinutes });
    const predictive = computePredictiveAlerts(bottlenecks, prevBottlenecksRef.current, avgStageMinutes);
    prevBottlenecksRef.current = bottlenecks;

    // Merge: predictive alerts appended, then re-sort by prioridade_score
    const merged = [...computed, ...predictive].sort((a, b) => b.prioridade_score - a.prioridade_score);

    // Remove dismissed/resolved
    const active = merged.filter(a => !dismissed.has(a.id) && !resolved.has(a.id));

    setAlerts(active);

    // Fire toasts for genuinely new alerts
    if (!silentMode) {
      for (const alert of active) {
        if (notifiedRef.current.has(alert.id)) continue;
        if (alert.tipo !== 'sla' && alert.tipo !== 'critico' && alert.tipo !== 'preditivo') continue;

        notifiedRef.current.add(alert.id);
        generatedAtRef.current.set(alert.id, Date.now());

        logAlertEvent({ alert_id: alert.id, tipo: alert.tipo, event: 'generated' });

        const executionRoute = alert.execution_id
          ? `/minha-mesa?task_id=${encodeURIComponent(alert.execution_id)}`
          : '/minha-mesa';

        showActionToast(
          alert.tipo === 'sla' ? 'error' : alert.tipo === 'preditivo' ? 'warning' : 'warning',
          alert.tipo === 'preditivo' ? alert.titulo : `🔥 ${alert.titulo}`,
          [
            {
              label: 'Abrir tarefa',
              variant: 'primary',
              onClick: () => {
                logAlertEvent({ alert_id: alert.id, tipo: alert.tipo, event: 'clicked' });
                navigate(executionRoute);
              },
            },
            {
              label: 'Ignorar',
              variant: 'ghost',
              onClick: () => {
                logAlertEvent({ alert_id: alert.id, tipo: alert.tipo, event: 'dismissed' });
                setDismissed(prev => new Set([...prev, alert.id]));
              },
            },
          ],
          8000
        );
      }
    }
  }, [workflows, bottlenecks, problems, overdueActions, avgStageMinutes, dismissed, resolved, silentMode, showActionToast, navigate]);

  // Run on data change
  useEffect(() => { runEngine(); }, [runEngine]);

  // Polling every 15s
  useEffect(() => {
    const interval = setInterval(runEngine, POLLING_MS);
    return () => clearInterval(interval);
  }, [runEngine]);

  const dismissAlert = useCallback((id: string) => {
    logAlertEvent({ alert_id: id, tipo: alerts.find(a => a.id === id)?.tipo ?? 'critico', event: 'dismissed' });
    setDismissed(prev => new Set([...prev, id]));
  }, [alerts]);

  const resolveAlert = useCallback((id: string) => {
    const genAt = generatedAtRef.current.get(id);
    const resolutionMinutes = genAt ? Math.round((Date.now() - genAt) / 60000) : undefined;
    logAlertEvent({
      alert_id: id,
      tipo: alerts.find(a => a.id === id)?.tipo ?? 'critico',
      event: 'resolved',
      resolution_minutes: resolutionMinutes,
    });
    setResolved(prev => new Set([...prev, id]));
  }, [alerts]);

  const onAlertClick = useCallback((alert: ComputedAlert) => {
    logAlertEvent({ alert_id: alert.id, tipo: alert.tipo, event: 'clicked' });
  }, []);

  const groups = groupAlerts(alerts);
  const topCritical = alerts.find(a => a.tipo === 'critico' || a.tipo === 'sla') ?? null;

  return { alerts, groups, topCritical, dismissAlert, resolveAlert, onAlertClick };
}
